import { useState } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };
  return [storedValue, setValue];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function saveValueToLocalStorage(key: string, value: any) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function getValueFromLocalStorage(key: string) {
  const value = window.localStorage.getItem(key);
  if (!value) {
    return null;
  }
  else {
    try {
      return JSON.parse(value);
    }
    catch (err) {
      console.warn('failed to parse stored local value', key, err);
      return null;
    }
  }
}

export function removeValueFromLocalStorage(key: string) {
  window.localStorage.removeItem(key);
}
